import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { Observable } from 'rxjs';
import { NotificationService } from '../notification/notification.service';
import { StorageService } from '../storage/storage.service';
import { DateUtils } from 'app/shared/utils';
import { ar, de, enGB, enUS, es, fr, it, ja, pt, ptBR, ru, zhCN, type Locale } from 'date-fns/locale';

export interface Terminology {
  programIdentifier?: string;
  name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private readonly defaultLanguage = 'en';
  private readonly languageStorageKey = 'miradi_lang';
  private readonly terminologyStorageKey = 'miradi_terminology';

  private dateFnsConfig: DateFnsConfigurationService;
  private notificationService: NotificationService;
  private storageService: StorageService;
  private translateService: TranslateService;

  currentLanguage: string;
  currentTerminology: Terminology;

  // Map of language codes to date-fns locale objects
  // Supports both language codes (e.g., 'en') and full locale codes (e.g., 'pt-PT')
  private readonly localeMap: { [key: string]: Locale } = {
    'ar': ar,
    'de': de,
    'de-DE': de,
    'en': enUS,
    'en-US': enUS,
    'en-GB': enGB,
    'es': es,
    'es-ES': es,
    'fr': fr,
    'fr-FR': fr,
    'it': it,
    'it-IT': it,
    'ja': ja,
    'ja-JP': ja,
    'pt': pt,
    'pt-PT': pt,
    'pt-BR': ptBR,
    'ru': ru,
    'ru-RU': ru,
    'zh': zhCN,
    'zh-CN': zhCN,
  };

  /**
   * Get the date-fns locale object for a given language code.
   * Supports full locale codes (e.g., 'pt-PT') and falls back to language code (e.g., 'pt')
   */
  private getDateFnsLocale(localeCode: string): Locale {
    if (!localeCode) return enUS;

    // Try exact match first (e.g., 'pt-PT')
    if (this.localeMap[localeCode]) {
      return this.localeMap[localeCode];
    }

    // Try language code only (e.g., 'pt' from 'pt-PT')
    const languageCode = localeCode.split('-')[0];
    if (this.localeMap[languageCode]) {
      return this.localeMap[languageCode];
    }

    // Fall back to English
    return enUS;
  }

  constructor(
    private injector: Injector,
  ) {

  }

  init(): void {
    this.dateFnsConfig = this.injector.get(DateFnsConfigurationService);
    this.notificationService = this.injector.get(NotificationService);
    this.storageService = this.injector.get(StorageService);
    this.translateService = this.injector.get(TranslateService);

    const locale = this.translateService.getBrowserLang(); // this.translateService.getBrowserCultureLang();

    this.translateService.setDefaultLang(this.defaultLanguage);

    // Set date-fns locale based on browser language
    console.log('Browser Locale: ' + locale);
    const dateFnsLocale = this.getDateFnsLocale(locale);
    DateUtils.setLocale(dateFnsLocale);
    this.dateFnsConfig.setLocale(dateFnsLocale);

    try {
      this.currentLanguage = this.storageService.getItem(this.languageStorageKey) || locale;
      this.translateService.use(this.currentLanguage);

      this.currentTerminology = this.storageService.getItem(this.terminologyStorageKey) || {};
    } catch (error) {
      this.showError(error);

      if ((window as any).awsRum) {
        (window as any).awsRum.recordEvent('CustomEvent', {
          message: error && error.message ? error.message : error,
          level: 'Warning'
        });
      }

      this.translateService.use(locale);
      this.currentTerminology = {};
    }
  }

  use(lang: string): Observable<any> {
    this.currentLanguage = lang;

    // Update date-fns locale when language changes
    const dateFnsLocale = this.getDateFnsLocale(lang);
    DateUtils.setLocale(dateFnsLocale);
    this.dateFnsConfig.setLocale(dateFnsLocale);

    try {
      this.storageService.setItem(this.languageStorageKey, this.currentLanguage);
    } catch (error) {
      this.showError(error);

      if ((window as any).awsRum) {
        (window as any).awsRum.recordEvent('CustomEvent', {
          message: error && error.message ? error.message : error,
          level: 'Warning'
        });
      }
    }

    return this.translateService.use(this.currentLanguage);
  }

  setDefaultTerminology(programIdentifier: string, name: string) {
    this.currentTerminology = {
      programIdentifier: programIdentifier,
      name: name,
    };
    try {
      this.storageService.setItem(this.terminologyStorageKey, this.currentTerminology);
    } catch (error) {
      this.showError(error);

      if ((window as any).awsRum) {
        (window as any).awsRum.recordEvent('CustomEvent', {
          message: error && error.message ? error.message : error,
          level: 'Warning'
        });
      }
    }
  }

  private showError(error: any) {
    console.warn(error);

    setTimeout(() => {
      this.notificationService.error(
        'Site Data / Cookies',
        'Your browser is currently set to block cookies. If you have issues accessing the site, please contact support.',
      );
    }, 1000);
  }

}
